/**
 * ⚡ USER ACTIONS CENTER - Bulk actions and follow-up management
 * Consolidates all user-related actions and follow-up workflows
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Notifications,
  Message,
  Block,
  CheckCircle,
  Warning,
  Schedule,
  Phone,
  Email,
  Assignment,
  TrendingUp,
  MoreVert,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const UserActionsCenter: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Mock follow-up data
  const followUpItems = [
    {
      id: '1',
      user: 'Sarah Kimani',
      avatar: '',
      type: 'Driver Onboarding',
      urgency: 'high',
      items: ['Complete background check', 'Submit insurance documents'],
      lastContact: '2024-11-25',
      daysOverdue: 6,
      phone: '+254-722-123-456',
      email: 'sarah.kimani@example.com'
    },
    {
      id: '2',
      user: 'James Okonkwo',
      avatar: '',
      type: 'Host Setup',
      urgency: 'medium',
      items: ['Upload vehicle photos', 'Set pricing'],
      lastContact: '2024-11-28',
      daysOverdue: 3,
      phone: '+234-803-456-789',
      email: 'james.okonkwo@example.com'
    },
    {
      id: '3',
      user: 'Grace Mensah',
      avatar: '',
      type: 'Verification',
      urgency: 'critical',
      items: ['Resubmit NIN document', 'Complete selfie verification'],
      lastContact: '2024-11-20',
      daysOverdue: 11,
      phone: '+233-24-789-012',
      email: 'grace.mensah@example.com'
    }
  ];

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const renderFollowUpTab = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Warning sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                43
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Critical Follow-ups
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Schedule sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                89
              </Typography>
              <Typography variant="body2" color="text.secondary">
                High Priority
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Message sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                156
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Contact
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                234
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Resolved Today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Urgency</TableCell>
              <TableCell>Pending Items</TableCell>
              <TableCell>Last Contact</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {followUpItems.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar>{item.user.charAt(0)}</Avatar>
                    <Box>
                      <Typography variant="subtitle2">{item.user}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Chip label={item.type} variant="outlined" size="small" />
                </TableCell>
                
                <TableCell>
                  <Chip 
                    label={item.urgency} 
                    color={getUrgencyColor(item.urgency) as any}
                    size="small"
                  />
                  {item.daysOverdue > 0 && (
                    <Typography variant="caption" color="error.main" display="block">
                      {item.daysOverdue} days overdue
                    </Typography>
                  )}
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {item.items.length} items pending
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.items[0]}{item.items.length > 1 && `, +${item.items.length - 1} more`}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {new Date(item.lastContact).toLocaleDateString()}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" color="primary">
                      <Phone />
                    </IconButton>
                    <IconButton size="small" color="secondary">
                      <Email />
                    </IconButton>
                    <IconButton size="small">
                      <MoreVert />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderBulkActionsTab = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Select multiple users from the User Directory to perform bulk actions here.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Communication Actions
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Message />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Send Bulk SMS" 
                    secondary="Send verification reminders or updates"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Send Bulk Email" 
                    secondary="Send newsletters or announcements"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Notifications />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Push Notifications" 
                    secondary="Send app notifications"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Actions
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Bulk Approve" 
                    secondary="Approve pending verifications"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Block />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Bulk Suspend" 
                    secondary="Suspend multiple accounts"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Assignment />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Export Data" 
                    secondary="Export selected user data"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Actions Center:</strong> This consolidates follow-up management from 
          FollowUpDashboardPage and admin notes from DriverManagementPage into a unified action center.
        </Typography>
      </Alert>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Follow-up Management" />
            <Tab label="Bulk Actions" />
            <Tab label="Admin Notes" />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          {renderFollowUpTab()}
        </TabPanel>
        
        <TabPanel value={currentTab} index={1}>
          {renderBulkActionsTab()}
        </TabPanel>
        
        <TabPanel value={currentTab} index={2}>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Admin Notes Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unified notes system across all user interactions
            </Typography>
          </Box>
        </TabPanel>
      </Card>
    </Box>
  );
};